import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  Search, 
  Users,
  Mail,
  Phone,
  TrendingUp,
  Building2,
  Trash2,
  Pencil,
  Lock
} from 'lucide-react';
import { customersAPI } from '../services/api';
import AddCustomerDialog from '../components/AddCustomerDialog';
import EditCustomerDialog from '../components/EditCustomerDialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const Customers = () => {
  const { canCreate, canEdit, canDelete } = useAuth();
  const [customersData, setCustomersData] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchCustomers();
  }, [selectedType]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const [customersRes, statsRes] = await Promise.all([
        customersAPI.getAll({ type: selectedType }),
        customersAPI.getStats()
      ]);
      setCustomersData(customersRes.data);
      setStats(statsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (customerId, customerName) => {
    try {
      await customersAPI.delete(customerId);
      toast({
        title: "Gelukt",
        description: `Klant "${customerName}" succesvol verwijderd`
      });
      fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast({
        title: "Fout",
        description: "Kan klant niet verwijderen",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setEditDialogOpen(true);
  };

  const getTypeLabel = (type) => {
    const labels = {
      'all': 'Alle Types',
      'corporate': 'Zakelijk',
      'individual': 'Particulier'
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'vip': 'VIP',
      'active': 'Actief',
      'inactive': 'Inactief'
    };
    return labels[status] || status;
  };

  const types = ['all', 'corporate', 'individual'];

  const filteredCustomers = customersData.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalRevenue = stats?.totalRevenue || 0;
  const vipCustomers = stats?.vipCustomers || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Klanten laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Klanten</h1>
          <p className="text-gray-500 mt-1">Beheer uw klantrelaties</p>
        </div>
        {canCreate() ? (
          <AddCustomerDialog onSuccess={fetchCustomers} />
        ) : (
          <div className="flex items-center text-gray-500 text-sm">
            <Lock className="w-4 h-4 mr-1" />
            Alleen lezen
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Totaal Klanten</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats?.totalCustomers || 0}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Totale Omzet</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">€{totalRevenue.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center shadow-lg">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">VIP Klanten</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{vipCustomers}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <Building2 className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Actieve Status</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats?.activeCustomers || 0}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-lg border-0">
        <CardHeader className="border-b bg-gradient-to-r from-red-50 to-yellow-50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-lg">
              <Users className="w-5 h-5 mr-2 text-red-600" />
              Klanten Overzicht
            </CardTitle>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Zoek klanten..."
                  className="pl-10 w-64"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-customers-input"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex border-b bg-gray-50 p-4 space-x-2 overflow-x-auto">
            {types.map(type => (
              <Button
                key={type}
                variant={selectedType === type ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedType(type)}
                className={selectedType === type ? 'bg-gradient-to-r from-red-600 to-yellow-600' : ''}
              >
                {getTypeLabel(type)}
              </Button>
            ))}
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Klantnaam</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Contact</TableHead>
                <TableHead className="font-semibold">Adres</TableHead>
                <TableHead className="font-semibold">Totaal Projecten</TableHead>
                <TableHead className="font-semibold">Totale Omzet</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.map((customer) => (
                <TableRow key={customer.id} className="hover:bg-gray-50 transition-colors duration-200">
                  <TableCell className="font-medium">{customer.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`${
                      customer.type === 'corporate' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      'bg-purple-50 text-purple-700 border-purple-200'
                    }`}>
                      {getTypeLabel(customer.type)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="w-3 h-3 mr-1" />
                        {customer.email}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="w-3 h-3 mr-1" />
                        {customer.phone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 max-w-xs truncate">{customer.address}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {customer.totalProjects}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-semibold">€{customer.totalRevenue.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge className={`${
                      customer.status === 'vip' ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100' :
                      'bg-green-100 text-green-700 hover:bg-green-100'
                    }`}>
                      {getStatusLabel(customer.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      {canEdit() && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-8"
                          onClick={() => handleEdit(customer)}
                          data-testid={`edit-customer-${customer.id}-btn`}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {canDelete() && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-customer-${customer.id}-btn`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Klant Verwijderen</AlertDialogTitle>
                              <AlertDialogDescription>
                                Weet je zeker dat je "{customer.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(customer.id, customer.name)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="confirm-delete-customer-btn"
                              >
                                Verwijderen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <EditCustomerDialog
        customer={selectedCustomer}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={fetchCustomers}
      />
    </div>
  );
};

export default Customers;
